import { useCallback, useRef } from 'react'

import { useNavigate, useParams } from 'react-router-dom'

import { F0WizardForm } from '@factorialco/f0-react'
import type { F0FormSubmitResult, F0WizardFormPerSectionSubmitArg } from '@factorialco/f0-react'
import { HasEmployeesToCreateRequestsDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateTrainingRequest } from 'modules/trainings/hooks/TrainingRequests/useCreateTrainingRequest'
import { trainingEvents } from 'modules/trainings/lib/events'

import { useFromCatalogForm } from './form'
import type { FromCatalogSchema, FromCatalogFormValues } from './form'

export interface Props {
  isOpen?: boolean
  onClose?: () => void
  back?: string
}

const FromCatalogWizard = ({ isOpen = true, onClose, back }: Props) => {
  const { trainingId } = useParams()
  const navigate = useNavigate()
  const { access, company, employee } = useCurrentSession()
  const { create } = useCreateTrainingRequest()
  const targetEmployeeId = employee?.id
  const latestFullDataRef = useRef<Partial<FromCatalogFormValues> | null>(null)

  const { data: employeesData, loading } = useQuery(HasEmployeesToCreateRequestsDocument, {
    fetchPolicy: 'network-only',
  })

  const showParticipantsStep =
    Number(employeesData?.employees.employeeNamesConnection?.totalCount ?? 0) > 0

  const showTrainingSelector = !trainingId

  const amplitudeEvents = trainingEvents.trainingRequest.addTrainingRequest.fromCatalog

  const closeWizard = useCallback(() => {
    if (onClose) {
      onClose()
    } else if (back) {
      navigate(back)
    }
  }, [navigate, back, onClose])

  const handleSubmit = useCallback(
    async (
      arg: F0WizardFormPerSectionSubmitArg<FromCatalogSchema>
    ): Promise<F0FormSubmitResult> => {
      // Capture latest form data for draft saving on close
      latestFullDataRef.current = arg.fullData

      // Per-section mode: only fire mutation on the last section (comments)
      if (arg.sectionId !== 'comments') {
        return { success: true }
      }

      const { trainingNeed } = arg.fullData.trainingNeed
      const training = arg.fullData.trainingSelector?.training
      const employeesSelection = arg.fullData.employees?.employeesSelection
      const { comments } = arg.data

      const resolvedTrainingId = trainingId ?? training?.id

      const result = await create({
        authorId: access.id,
        companyId: String(company.id),
        status: 'review',
        subsidized: false,
        competencyIds: [],
        employeesSelection: employeesSelection ?? {
          type: 'manual',
          ids: targetEmployeeId ? [targetEmployeeId] : [],
        },
        trainingId: resolvedTrainingId,
        trainingName: training?.name || undefined,
        trainingNeed,
        comments,
      })

      let formErrors: Record<string, string> = {}
      const handled = handleResult(result, (errors) => {
        formErrors = errors
      })

      if (!handled.success) {
        return { success: false, errors: formErrors }
      }

      track(amplitudeEvents.submit, { from: 'my_request' })

      app.setFlash({
        type: 'correct',
        message: i18n.t('trainings.training_requests.create.success_messages.pending'),
      })

      latestFullDataRef.current = null
      closeWizard()
      return { success: true }
    },
    [
      create,
      access.id,
      company.id,
      targetEmployeeId,
      trainingId,
      amplitudeEvents.submit,
      closeWizard,
    ]
  )

  const handleClose = useCallback(async () => {
    const fullData = latestFullDataRef.current
    if (fullData) {
      const resolvedTrainingId = trainingId ?? fullData.trainingSelector?.training?.id

      const result = await create({
        authorId: access.id,
        companyId: String(company.id),
        status: 'draft',
        subsidized: false,
        competencyIds: [],
        employeesSelection: fullData.employees?.employeesSelection ?? {
          type: 'manual',
          ids: targetEmployeeId ? [targetEmployeeId] : [],
        },
        trainingId: resolvedTrainingId,
        trainingName: fullData.trainingSelector?.training?.name || undefined,
        trainingNeed: fullData.trainingNeed?.trainingNeed,
        comments: fullData.comments?.comments,
      })

      const handled = handleResult(result, () => undefined)
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('trainings.training_requests.create.success_messages.draft'),
        })
      }
    }

    track(amplitudeEvents.cancel, { from: 'my_request' })
    closeWizard()
  }, [
    create,
    access.id,
    company.id,
    targetEmployeeId,
    trainingId,
    amplitudeEvents.cancel,
    closeWizard,
  ])

  const handleStepChanged = useCallback(() => {
    track(amplitudeEvents.next, { from: 'my_request' })
  }, [amplitudeEvents.next])

  const { formDefinition, steps } = useFromCatalogForm({
    showParticipantsStep,
    showTrainingSelector,
    defaultEmployeeId: targetEmployeeId,
    defaultTrainingId: trainingId,
    onSubmit: handleSubmit,
  })

  if (loading) {
    return <Loading />
  }

  return (
    <F0WizardForm
      isOpen={isOpen}
      title={i18n.t('trainings.training_requests.create.training_request_title')}
      formDefinition={formDefinition}
      steps={steps}
      onClose={handleClose}
      onStepChanged={handleStepChanged}
    />
  )
}

export default FromCatalogWizard
